import { create } from "zustand";
import { ProductsAPI } from "../Helpers/ConfigProvider";

const useHamperStore = create((set, get) => ({
  selectedHamper: null,
  errors: [],
  loading: false,
  // Map of productId -> selected option value ID
  productSelections: {},

  getHamperByFriendlyName: async (friendlyName) => {
    set({ loading: true, errors: [] });

    let response;
    try {
      response = await fetch(`${ProductsAPI}/Hampers/${friendlyName}`);
      if (!response.ok) {
        const error = await response.json();
        set({ errors: [...get().errors, error], loading: false });
        return;
      }
    } catch (error) {
      set({
        errors: [...get().errors, "Network error while fetching hamper"],
        loading: false,
      });
      return;
    }
    try {
      const data = await response.json();

      // Initialize default selections (first option value for each product with options)
      const initialSelections = {};
      if (data.Products) {
        data.Products.forEach(({ Product }) => {
          if (Product.Options && Product.Options.length > 0) {
            const productOptionsMap = {};

            Product.Options.forEach((option) => {
              // If option has only one value, auto-select it
              if (option.OptionValues && option.OptionValues.length === 1) {
                productOptionsMap[option.Id] = option.OptionValues[0].Id;
              }
            });

            // Only add to initialSelections if we found any auto-selectable options
            if (Object.keys(productOptionsMap).length > 0) {
              initialSelections[Product.Id] = productOptionsMap;
            }
          }
        });
      }

      set({
        selectedHamper: data,
        productSelections: initialSelections,
        errors: [],
        loading: false,
      });
      return data;
    } catch (error) {
      set({
        errors: [...get().errors, "Failed to get hamper"],
        loading: false,
      });
      return null;
    }
  },

  updateProductSelection: (productId, optionId, optionValueId) => {
    const currentProduct = get().productSelections[productId] || {};

    set({
      productSelections: {
        ...get().productSelections,
        [productId]: {
          ...currentProduct,
          [optionId]: optionValueId,
        },
      },
    });
  },

  getProductSelection: (productId, optionId) => {
    return get().productSelections[productId]?.[optionId] || "";
  },

  buildProductSelectionSummary: () => {
    const { selectedHamper, productSelections } = get();
    if (!selectedHamper || !selectedHamper.Products) return {};

    const summary = {};

    selectedHamper.Products.forEach(({ Product }) => {
      const productOptions = productSelections[Product.Id];

      if (!productOptions || Object.keys(productOptions).length === 0) {
        summary[Product.Name] = null;
        return;
      }

      const selectedOptionsArray = [];
      for (const option of Product.Options || []) {
        const selectedValueId = productOptions[option.Id];
        if (selectedValueId) {
          const selectedValue = option.OptionValues.find(
            (val) => val.Id === selectedValueId,
          );
          if (selectedValue) {
            selectedOptionsArray.push({
              optionName: option.OptionName,
              value: selectedValue.Value,
            });
          }
        }
      }

      summary[Product.Name] =
        selectedOptionsArray.length > 0 ? selectedOptionsArray : null;
    });

    return summary;
  },

  isHamperReadyToOrder: () => {
    const { selectedHamper, productSelections } = get();
    if (!selectedHamper || !selectedHamper.Products) return false;

    // Check each product has all required options selected
    for (const { Product } of selectedHamper.Products) {
      const productOptions = Product.Options || [];

      // If product has no options, it's valid
      if (productOptions.length === 0) continue;

      // Get selections for this product
      const selectedOptions = productSelections[Product.Id];

      // If no selections exist for a product with options, it's invalid
      if (!selectedOptions) return false;

      // Check that every option has a selection
      for (const option of productOptions) {
        const selectedValueId = selectedOptions[option.Id];
        if (!selectedValueId) {
          return false; // Missing selection for this option
        }
      }
    }

    return true; // All products have all options selected
  },
}));

export default useHamperStore;
